import React from 'react';
import { Loader2, LoaderCircle, Loader, Orbit } from 'lucide-react';

// Define spinner types for flexibility
type SpinnerType = 'default' | 'full-screen' | 'inline' | 'overlay';
type SpinnerVariant = 'primary' | 'secondary' | 'destructive' | 'ghost';

interface LoadingSpinnerProps {
    type?: SpinnerType;
    variant?: SpinnerVariant;
    message?: string;
    className?: string;
    iconSize?: number;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
    type = 'default',
    variant = 'primary',
    message,
    className = '',
    iconSize = 24
}) => {
    // Color and style variants
    const variantStyles = {
        primary: 'text-blue-500',
        secondary: 'text-gray-500',
        destructive: 'text-red-500',
        ghost: 'text-gray-300'
    };

    // Render different spinner layouts based on type
    const renderSpinner = () => {
        const baseSpinnerClass = `animate-spin ${variantStyles[variant]} ${className}`;

        switch (type) {
            case 'full-screen':
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 dark:bg-black/50">
                        <div className="flex flex-col items-center justify-center">
                            <Loader2 size={iconSize} className={baseSpinnerClass} />
                            {message && <p className="mt-4 text-sm text-gray-600">{message}</p>}
                        </div>
                    </div>
                );

            case 'inline':
                return (
                    <span className="inline-flex items-center">
                        <LoaderCircle size={iconSize} className={baseSpinnerClass} />
                        {message && <span className="ml-2 text-sm">{message}</span>}
                    </span>
                );

            case 'overlay':
                return (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/30 dark:bg-black/30">
                        <Loader size={iconSize} className={baseSpinnerClass} />
                        {message && <p className="ml-3 text-sm">{message}</p>}
                    </div>
                );

            default:
                return <Orbit size={iconSize} className={baseSpinnerClass} />;
        }
    };

    return renderSpinner();
};

// Preset loading spinners for common use cases
export const FullScreenLoader: React.FC<Omit<LoadingSpinnerProps, 'type'>> = (props) => (
    <LoadingSpinner type="full-screen" {...props} />
);

export const InlineLoader: React.FC<Omit<LoadingSpinnerProps, 'type'>> = (props) => (
    <LoadingSpinner type="inline" {...props} />
);

export const OverlayLoader: React.FC<Omit<LoadingSpinnerProps, 'type'>> = (props) => (
    <LoadingSpinner type="overlay" {...props} />
);

export default LoadingSpinner;